/**
 * Public quiz play page.
 * Handles question delivery, answer checking, score summaries, and attempt persistence.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Space,
  Tag,
  message,
  Spin,
  Typography,
  Select,
  Progress,
  Checkbox,
  Image,
} from 'antd';
import {
  PlayCircleOutlined,
  ReloadOutlined,
  HomeOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  RightOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  QuestionCircleOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import RadarChart from '../../components/RadarChart/RadarChart';
import quizCollectionsService from '../../services/quizCollectionsService';
import { getFileUrl } from '../../utils/urlUtils';
import styles from './Quiz.module.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Format elapsed quiz time for the live timer display.
 *
 * @param {number} seconds - Elapsed time in seconds.
 * @returns {string} Zero-padded MM:SS label.
 */
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Interactive quiz runner for published quiz collections.
 *
 * @returns {JSX.Element} Public quiz play experience.
 */
const QuizPlay = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const timerRef = useRef(null);

  // Collection info
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answers, setAnswers] = useState([]); // Enriched answer objects for results dashboard

  // Session token for server-side attempt persistence
  const [playSessionToken, setPlaySessionToken] = useState(null);
  // Timestamp when current question was displayed (for client-reported per-question duration)
  const questionDisplayTimeRef = useRef(null);

  // Settings
  const [questionCount, setQuestionCount] = useState(10);
  const [selectedTopicId, setSelectedTopicId] = useState(null);

  // Timer
  const [elapsedTime, setElapsedTime] = useState(0);

  // Fetch collection info
  useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true);
      try {
        const data = await quizCollectionsService.getPublicQuizCollection(id);
        setCollection(data);
        setQuestionCount(Math.min(10, data.questionCount));
      } catch (err) {
        setError(t('quiz.notFoundOrPrivate', 'Quiz collection not found or not published'));
      } finally {
        setLoading(false);
      }
    };
    fetchCollection();
  }, [id, t]);

  // Timer effect
  useEffect(() => {
    if (quizStarted && !quizFinished) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [quizStarted, quizFinished]);

  // Start quiz
  const handleStartQuiz = async () => {
    try {
      setLoading(true);
      const result = await quizCollectionsService.startQuizPlay(id, {
        questionCount,
        topicId: selectedTopicId || undefined,
      });

      setPlaySessionToken(result.playSessionToken || null);
      setQuestions(result.questions || []);
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setSelectedChoices([]);
      setShowAnswer(false);
      setAnswers([]);
      setElapsedTime(0);
      questionDisplayTimeRef.current = Date.now();
    } catch (err) {
      message.error(t('quiz.startError', 'Failed to start quiz'));
    } finally {
      setLoading(false);
    }
  };

  // Select/deselect choice
  const handleChoiceSelect = (choiceId) => {
    if (showAnswer) return;
    
    const currentQuestion = questions[currentQuestionIndex];
    
    if (currentQuestion.isMultiSelect) {
      // Multi-select: toggle choice
      setSelectedChoices(prev => 
        prev.includes(choiceId)
          ? prev.filter(id => id !== choiceId)
          : [...prev, choiceId]
      );
    } else {
      // Single-select: replace
      setSelectedChoices([choiceId]);
    }
  };

  // Check answer
  const handleCheckAnswer = async () => {
    if (selectedChoices.length === 0) {
      message.warning(t('quiz.selectAnswer', 'Please select an answer'));
      return;
    }

    try {
      const currentQuestion = questions[currentQuestionIndex];
      const clientDurationMs = questionDisplayTimeRef.current
        ? Date.now() - questionDisplayTimeRef.current
        : null;

      const result = await quizCollectionsService.checkAnswer(
        currentQuestion.id,
        selectedChoices
      );

      setShowAnswer(true);
      setAnswers(prev => [...prev, {
        questionId: currentQuestion.id,
        questionCode: currentQuestion.code,
        questionBody: currentQuestion.body,
        topic: currentQuestion.topic,
        subtopic: currentQuestion.subtopic,
        levelOfDifficultyName: currentQuestion.levelOfDifficultyName,
        levelOfDifficultyColor: currentQuestion.levelOfDifficultyColor,
        weights: currentQuestion.weights || [],
        choices: currentQuestion.choices || [],
        selectedChoiceIds: selectedChoices,
        isCorrect: result.isCorrect,
        correctChoiceIds: result.correctChoiceIds,
        isMultiSelect: currentQuestion.isMultiSelect,
        clientDurationMs,
      }]);
    } catch (err) {
      message.error(t('quiz.checkError', 'Failed to check answer'));
    }
  };

  // Persist attempt to server (non-blocking — called on finish)
  const persistAttempt = (completedAnswers, sessionToken) => {
    if (!sessionToken) return;
    quizCollectionsService.finishQuiz(id, {
      playSessionToken: sessionToken,
      answers: completedAnswers.map(a => ({
        questionId: a.questionId,
        selectedChoiceIds: a.selectedChoiceIds,
        clientDurationMs: a.clientDurationMs ?? null,
      })),
    }).catch(err => {
      // Non-blocking: keep the results screen visible, but tell the user analytics were not saved.
      console.warn('Quiz attempt save failed:', err);
      message.warning(t(
        'quiz.saveAnalyticsWarning',
        'Your result was shown, but saving quiz analytics failed.'
      ));
    });
  };

  // Next question
  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedChoices([]);
      setShowAnswer(false);
      questionDisplayTimeRef.current = Date.now();
    } else {
      // Quiz finished — show results immediately, persist attempt in background
      setQuizFinished(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      persistAttempt(answers, playSessionToken);
    }
  };

  // Restart quiz
  const handleRestartQuiz = () => {
    setQuizStarted(false);
    setQuizFinished(false);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedChoices([]);
    setShowAnswer(false);
    setAnswers([]);
    setElapsedTime(0);
    setPlaySessionToken(null);
    questionDisplayTimeRef.current = null;
  };

  // Calculate results
  const correctCount = answers.filter(a => a.isCorrect).length;
  const totalCount = answers.length;
  const scorePercent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;

  const getScoreClass = () => {
    if (scorePercent >= 90) return 'excellent';
    if (scorePercent >= 70) return 'good';
    if (scorePercent >= 50) return 'average';
    return 'poor';
  };

  const getPerformanceLevel = () => {
    if (scorePercent >= 90) return { label: t('quiz.expert', 'Expert'), emoji: '🏆' };
    if (scorePercent >= 70) return { label: t('quiz.proficient', 'Proficient'), emoji: '⭐' };
    if (scorePercent >= 50) return { label: t('quiz.developing', 'Developing'), emoji: '📈' };
    return { label: t('quiz.needsPractice', 'Needs Practice'), emoji: '💪' };
  };

  const getScoreMessage = () => {
    if (scorePercent >= 90) return t('quiz.excellent', 'Excellent! Outstanding performance!');
    if (scorePercent >= 70) return t('quiz.good', 'Good job! Well done!');
    if (scorePercent >= 50) return t('quiz.average', 'Not bad! Keep practicing!');
    return t('quiz.needsWork', 'Keep learning! You can do better!');
  };

  // Difficulty breakdown
  const difficultyBreakdown = useMemo(() => {
    const breakdown = {};
    answers.forEach(a => {
      const level = a.levelOfDifficultyName || t('quiz.unrated', 'Unrated');
      if (!breakdown[level]) {
        breakdown[level] = { correct: 0, total: 0, color: a.levelOfDifficultyColor || '#d9d9d9' };
      }
      breakdown[level].total++;
      if (a.isCorrect) breakdown[level].correct++;
    });
    return breakdown;
  }, [answers, t]);

  // Topic skill profile data (for radar chart and bar charts)
  const topicProfileData = useMemo(() => {
    const topics = collection?.topics || [];
    if (topics.length === 0) return null;

    const baseline = {}; // possible contribution per topic (0-100)
    const achieved = {}; // achieved contribution per topic (0-100)
    const topicTotals = {}; // { topicId: { possible: float, achieved: float, questionCount: int } }

    topics.forEach(tp => {
      topicTotals[tp.id] = { possible: 0, achieved: 0, questionCount: 0 };
    });

    answers.forEach(a => {
      if (a.weights && a.weights.length > 0) {
        a.weights.forEach(w => {
          if (topicTotals[w.topicId] !== undefined) {
            topicTotals[w.topicId].possible += w.weight;
            topicTotals[w.topicId].questionCount++;
            if (a.isCorrect) {
              topicTotals[w.topicId].achieved += w.weight;
            }
          }
        });
      }
    });

    // Check if any topic has data
    let hasData = false;
    topics.forEach(tp => {
      const total = topicTotals[tp.id];
      if (total.questionCount > 0) {
        hasData = true;
        baseline[tp.id] = Math.round((total.possible / answers.length) * 100);
        achieved[tp.id] = Math.round((total.achieved / answers.length) * 100);
      } else {
        baseline[tp.id] = 0;
        achieved[tp.id] = 0;
      }
    });

    if (!hasData) return null;

    return {
      topics: topics.map((tp, i) => ({
        id: tp.id,
        name: tp.name,
        color: undefined, // will use default palette
      })),
      baseline,
      achieved,
      topicTotals,
    };
  }, [answers, collection]);

  // Loading state
  if (loading && !collection) {
    return (
      <PagesWrapper>
        <div className={styles.playContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
            <Spin size="large" />
          </div>
        </div>
      </PagesWrapper>
    );
  }

  // Error state
  if (error) {
    return (
      <PagesWrapper>
        <div className={styles.playContainer}>
          <div className={styles.resultsContainer}>
            <QuestionCircleOutlined style={{ fontSize: 64, color: '#ff4d4f', marginBottom: 24 }} />
            <Title level={3}>{t('quiz.notAvailable', 'Quiz Not Available')}</Title>
            <Text type="secondary">{error}</Text>
            <div className={styles.resultsActions} style={{ marginTop: 24 }}>
              <Button icon={<HomeOutlined />} onClick={() => navigate('/')}>
                {t('common.goHome', 'Go Home')}
              </Button>
            </div>
          </div>
        </div>
      </PagesWrapper>
    );
  }

  // Results screen
  if (quizFinished) {
    const scoreClass = getScoreClass();
    const performance = getPerformanceLevel();

    return (
      <PagesWrapper>
        <div className={styles.playContainer}>
          <div className={styles.resultsDashboard}>
            {/* Hero Section */}
            <div className={styles.resultsHero}>
              <TrophyOutlined style={{ fontSize: 40, color: '#faad14', marginBottom: 12 }} />
              <Title level={3} style={{ margin: '0 0 8px' }}>
                {t('quiz.quizComplete', 'Quiz Complete!')}
              </Title>

              <div className={`${styles.scoreCircle} ${styles[scoreClass]}`}>
                <span className={styles.scoreValue}>{scorePercent}%</span>
                <span className={styles.scoreLabel}>{t('quiz.score', 'Score')}</span>
              </div>

              <div className={`${styles.performanceBadge} ${styles[scoreClass]}`}>
                <span>{performance.emoji}</span>
                <span>{performance.label}</span>
              </div>

              <Text type="secondary" style={{ fontSize: 14 }}>{getScoreMessage()}</Text>
            </div>

            {/* Stats Summary Row */}
            <div className={styles.statsSummary}>
              <div className={styles.statsSummaryItem}>
                <span className={styles.statsSummaryValue} style={{ color: '#52c41a' }}>
                  {correctCount}
                </span>
                <span className={styles.statsSummaryLabel}>
                  {t('quiz.correct', 'Correct')}
                </span>
              </div>
              <div className={styles.statDivider} />
              <div className={styles.statsSummaryItem}>
                <span className={styles.statsSummaryValue} style={{ color: '#ff4d4f' }}>
                  {totalCount - correctCount}
                </span>
                <span className={styles.statsSummaryLabel}>
                  {t('quiz.incorrect', 'Incorrect')}
                </span>
              </div>
              <div className={styles.statDivider} />
              <div className={styles.statsSummaryItem}>
                <span className={styles.statsSummaryValue}>
                  <ClockCircleOutlined style={{ fontSize: 16, marginRight: 4 }} />
                  {formatTime(elapsedTime)}
                </span>
                <span className={styles.statsSummaryLabel}>
                  {t('quiz.time', 'Time')}
                </span>
              </div>
              {Object.keys(difficultyBreakdown).length > 0 && (
                <>
                  <div className={styles.statDivider} />
                  <div className={styles.statsSummaryItem}>
                    <div className={styles.difficultyBreakdown}>
                      {Object.entries(difficultyBreakdown).map(([level, data]) => (
                        <span key={level} className={styles.difficultyItem}>
                          <span className={styles.difficultyDot} style={{ backgroundColor: data.color }} />
                          <span>{level}</span>
                          <span className={styles.difficultyScore}>
                            {data.correct}/{data.total}
                          </span>
                        </span>
                      ))}
                    </div>
                    <span className={styles.statsSummaryLabel}>
                      {t('quiz.difficulty', 'Difficulty')}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Skill Profile Radar Chart */}
            {topicProfileData && topicProfileData.topics.length >= 3 && (
              <div className={styles.radarChartSection}>
                <RadarChart
                  topics={topicProfileData.topics}
                  baselineData={topicProfileData.baseline}
                  achievedData={topicProfileData.achieved}
                  showLegend={true}
                  showDetails={true}
                  title={`🎯 ${t('quiz.skillProfile', 'Your Skill Profile')}`}
                  baselineLabel={t('quiz.topicCoverage', 'Topic Coverage')}
                  achievedLabel={t('quiz.yourPerformance', 'Your Performance')}
                />
              </div>
            )}

            {/* Topic Relevance Profile (bar charts) */}
            {topicProfileData && (
              <div className={styles.relevanceSection}>
                <div className={styles.relevanceTitle}>
                  {t('quiz.relevanceProfile', 'Quiz Relevance Profile')}
                </div>
                <div className={styles.relevanceGrid}>
                  {topicProfileData.topics.map((topic, i) => {
                    const totals = topicProfileData.topicTotals[topic.id];
                    const pct = totals.possible > 0
                      ? Math.round((totals.achieved / totals.possible) * 100)
                      : 0;
                    const DEFAULT_COLORS = [
                      '#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#eb2f96',
                      '#fa541c', '#2f54eb', '#a0d911', '#fa8c16', '#9254de', '#f5222d',
                    ];
                    const color = DEFAULT_COLORS[i % DEFAULT_COLORS.length];

                    return (
                      <div key={topic.id} className={styles.relevanceItem}>
                        <div className={styles.relevanceHeader}>
                          <span className={styles.relevanceLabel}>{topic.name}</span>
                          <span className={styles.relevancePercent} style={{ color }}>
                            {pct}%
                          </span>
                        </div>
                        <div className={styles.relevanceBar}>
                          <div
                            className={styles.relevanceFill}
                            style={{ width: `${pct}%`, backgroundColor: color }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Question Review */}
            <div className={styles.reviewCard}>
              <div className={styles.reviewTitle}>
                {t('quiz.questionReview', 'Question Review')}
              </div>
              <div className={styles.reviewList}>
                {answers.map((answer, index) => {
                  const selectedTexts = answer.choices
                    .filter(c => answer.selectedChoiceIds.includes(c.id))
                    .map(c => c.text)
                    .filter(Boolean);

                  // Strip HTML tags from body for display
                  const plainBody = answer.questionBody
                    ? answer.questionBody.replace(/<[^>]*>/g, '').slice(0, 120)
                    : '';

                  return (
                    <div
                      key={answer.questionId}
                      className={`${styles.reviewItem} ${answer.isCorrect ? styles.reviewCorrect : styles.reviewIncorrect}`}
                    >
                      <div className={styles.reviewHeader}>
                        <span className={styles.reviewNumber}>
                          Q{index + 1} {answer.questionCode && `(${answer.questionCode})`}
                        </span>
                        {answer.isCorrect
                          ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                          : <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
                        }
                      </div>
                      <span className={styles.reviewQuestion}>
                        {plainBody}{plainBody.length >= 120 ? '…' : ''}
                      </span>
                      {/* Show user's answer */}
                      <span className={`${styles.reviewAnswer} ${answer.isCorrect ? styles.reviewAnswerCorrect : styles.reviewAnswerIncorrect}`}>
                        {answer.isCorrect
                          ? <><CheckCircleFilled style={{ marginRight: 4 }} /> {selectedTexts.join(', ') || t('quiz.imageAnswer', '(Image answer)')}</>
                          : <><CloseCircleFilled style={{ marginRight: 4 }} /> {selectedTexts.join(', ') || t('quiz.imageAnswer', '(Image answer)')}</>
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.resultsActions}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={handleRestartQuiz}
                size="large"
              >
                {t('quiz.playAgain', 'Play Again')}
              </Button>
              <Button
                icon={<HomeOutlined />}
                onClick={() => navigate('/')}
                size="large"
              >
                {t('common.goHome', 'Go Home')}
              </Button>
            </div>
          </div>
        </div>
      </PagesWrapper>
    );
  }

  // Quiz in progress
  if (quizStarted && questions.length > 0) {
    const currentQuestion = questions[currentQuestionIndex];
    const currentAnswer = showAnswer ? answers[answers.length - 1] : null;

    return (
      <PagesWrapper>
        <div className={styles.playContainer}>
          {/* Progress bar */}
          <div className={styles.quizProgress}>
            <div className={styles.progressInfo}>
              <div className={styles.progressItem}>
                <span className={styles.progressLabel}>{t('quiz.question', 'Question')}</span>
                <span className={styles.progressValue}>
                  {currentQuestionIndex + 1}/{questions.length}
                </span>
              </div>
              <div className={styles.progressItem}>
                <span className={styles.progressLabel}>{t('quiz.correct', 'Correct')}</span>
                <span className={styles.progressValue} style={{ color: '#52c41a' }}>
                  {correctCount}
                </span>
              </div>
            </div>
            <div className={styles.timer}>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              {formatTime(elapsedTime)}
            </div>
          </div>

          <Progress 
            percent={(currentQuestionIndex / questions.length) * 100} 
            showInfo={false}
            strokeColor="#1890ff"
            style={{ marginBottom: 24 }}
          />

          {/* Question */}
          <div className={styles.questionContainer}>
            <div className={styles.questionHeader}>
              <span className={styles.questionNumber}>
                {currentQuestion.code}
              </span>
              <Space>
                {currentQuestion.levelOfDifficultyName && (
                  <Tag color={currentQuestion.levelOfDifficultyColor || 'default'}>
                    {currentQuestion.levelOfDifficultyName}
                  </Tag>
                )}
                {currentQuestion.topic && (
                  <Tag>{currentQuestion.topic}</Tag>
                )}
                {currentQuestion.version && (
                  <span className={styles.versionTag}>v{currentQuestion.version}</span>
                )}
              </Space>
            </div>

            {/* Category breadcrumb: Topic • Subtopic */}
            {(currentQuestion.topic || currentQuestion.subtopic) && (
              <div className={styles.categoryBreadcrumb}>
                {currentQuestion.topic && (
                  <span className={styles.breadcrumbTopic}>{currentQuestion.topic}</span>
                )}
                {currentQuestion.topic && currentQuestion.subtopic && (
                  <span className={styles.breadcrumbSeparator}>•</span>
                )}
                {currentQuestion.subtopic && (
                  <span className={styles.breadcrumbItem}>{currentQuestion.subtopic}</span>
                )}
              </div>
            )}

            <div className={styles.questionStemLayout}>
              {currentQuestion.imageUrl && (
                <div className={styles.questionImageColumn}>
                  <Image
                    src={getFileUrl(currentQuestion.imageUrl)}
                    alt=""
                    className={styles.questionImage}
                  />
                </div>
              )}

              <div className={styles.questionTextColumn}>
                <div
                  className={styles.questionText}
                  dangerouslySetInnerHTML={{ __html: currentQuestion.body }}
                />

                {showAnswer && currentQuestion.pdfUrl && (
                  <a
                    href={getFileUrl(currentQuestion.pdfUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.referenceLink}
                  >
                    <FilePdfOutlined />
                    <span>{t('quiz.reference', 'Reference')}</span>
                  </a>
                )}
              </div>
            </div>

            {currentQuestion.isMultiSelect && (
              <div className={styles.multiSelectHint}>
                <CheckCircleFilled style={{ color: '#1890ff', marginRight: 8 }} />
                {t('quiz.multiSelectHint', 'Select all correct answers')}
              </div>
            )}

            {/* Choices */}
            <div className={styles.choicesContainer}>
              {currentQuestion.choices?.map((choice) => {
                const isSelected = selectedChoices.includes(choice.id);
                const isCorrectChoice = currentAnswer?.correctChoiceIds?.includes(choice.id);
                const wasSelectedIncorrectly = showAnswer && isSelected && !isCorrectChoice;

                let choiceClass = styles.choiceOption;
                if (isSelected && !showAnswer) choiceClass += ` ${styles.selected}`;
                if (showAnswer && isCorrectChoice) choiceClass += ` ${styles.correct}`;
                if (wasSelectedIncorrectly) choiceClass += ` ${styles.incorrect}`;
                if (showAnswer) choiceClass += ` ${styles.disabled}`;

                return (
                  <div
                    key={choice.id}
                    className={choiceClass}
                    onClick={() => handleChoiceSelect(choice.id)}
                  >
                    <div className={styles.choiceIndicator}>
                      {showAnswer && isCorrectChoice && <CheckCircleFilled style={{ color: '#52c41a' }} />}
                      {wasSelectedIncorrectly && <CloseCircleFilled style={{ color: '#ff4d4f' }} />}
                      {!showAnswer && isSelected && <CheckCircleFilled style={{ color: '#1890ff' }} />}
                    </div>
                    {choice.imageUrl ? (
                      <Image
                        src={getFileUrl(choice.imageUrl)}
                        alt=""
                        className={styles.choiceImage}
                        preview={false}
                      />
                    ) : null}
                    <span className={styles.choiceText}>{choice.text}</span>
                  </div>
                );
              })}
            </div>

            {/* Law / Normen Reference Box */}
            {currentQuestion.law && (
              <div className={styles.normenBox}>
                <div className={styles.normenHeader}>
                  <FileTextOutlined className={styles.normenIcon} />
                  <span className={styles.normenTitle}>
                    {t('quiz.legalReference', 'Legal / Regulatory Reference')}
                  </span>
                </div>
                <span className={styles.normenText}>{currentQuestion.law}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className={styles.quizActions}>
            <div>
              {showAnswer && (
                <Tag 
                  color={currentAnswer?.isCorrect ? 'success' : 'error'}
                  icon={currentAnswer?.isCorrect ? <CheckCircleFilled /> : <CloseCircleFilled />}
                  style={{ fontSize: 14, padding: '4px 12px' }}
                >
                  {currentAnswer?.isCorrect 
                    ? t('quiz.correctAnswer', 'Correct!') 
                    : t('quiz.incorrectAnswer', 'Incorrect')}
                </Tag>
              )}
            </div>
            <div>
              {!showAnswer ? (
                <Button 
                  type="primary" 
                  onClick={handleCheckAnswer}
                  disabled={selectedChoices.length === 0}
                  size="large"
                >
                  {t('quiz.checkAnswer', 'Check Answer')}
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={handleNextQuestion}
                  icon={<RightOutlined />}
                  size="large"
                >
                  {currentQuestionIndex < questions.length - 1 
                    ? t('quiz.nextQuestion', 'Next Question')
                    : t('quiz.viewResults', 'View Results')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </PagesWrapper>
    );
  }

  // Setup screen
  return (
    <PagesWrapper>
      <div className={styles.playContainer}>
        <div className={styles.playHeader}>
          <QuestionCircleOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
          <Title level={2} className={styles.playTitle}>
            {collection?.name}
          </Title>
          <Text type="secondary">
            {t('quiz.availableQuestions', '{{count}} questions available', { count: collection?.questionCount || 0 })}
          </Text>
        </div>

        <div className={styles.playSetup}>
          <div className={styles.setupOption}>
            <span className={styles.setupLabel}>{t('quiz.numberOfQuestions', 'Number of Questions')}</span>
            <Select
              value={questionCount}
              onChange={setQuestionCount}
              style={{ width: '100%' }}
              options={[5, 10, 15, 20, 25, 30].filter(n => n <= (collection?.questionCount || 0)).map(n => ({
                value: n,
                label: `${n} ${t('quiz.questions', 'questions')}`,
              }))}
            />
          </div>

          {collection?.topics && collection.topics.length > 0 && (
            <div className={styles.setupOption}>
              <span className={styles.setupLabel}>{t('quiz.filterByTopic', 'Filter by Topic (optional)')}</span>
              <Select
                value={selectedTopicId}
                onChange={setSelectedTopicId}
                style={{ width: '100%' }}
                allowClear
                placeholder={t('quiz.allTopics', 'All Topics')}
                options={collection.topics.map(t => ({
                  value: t.id,
                  label: t.name,
                }))}
              />
            </div>
          )}

          <Button
            type="primary"
            size="large"
            icon={<PlayCircleOutlined />}
            onClick={handleStartQuiz}
            loading={loading}
            className={styles.startButton}
            block
          >
            {t('quiz.startQuiz', 'Start Quiz')}
          </Button>
        </div>
      </div>
    </PagesWrapper>
  );
};

export default QuizPlay;
